/**
 * Date and time-slot helpers for the Taiken calendar.
 *
 * Every function here is pure so the scheduling rules can be tested without a DOM.
 */

export function pad2(n) {
    return String(n).padStart(2, '0');
}

/**
 * Format a Date as YYYY-MM-DD using its local calendar fields.
 *
 * @param {Date} date Date to format.
 * @returns {string}
 */
export function toISODate(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * Parse YYYY-MM-DD into a local Date at midnight.
 *
 * @param {string} iso Date string.
 * @returns {Date|null}
 */
export function parseISODate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!match) {
        return null;
    }
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Parse an "HH:MM-HH:MM" slot into minute offsets and the hours it covers.
 *
 * @param {string} slot Raw slot string.
 * @returns {{start:number,end:number,startHour:number,lastHour:number,label:string}|null}
 */
export function parseSlot(slot) {
    // PHP normalises to a hyphen, but the site's own copy also uses ~ / 〜.
    const match = /^(\d{1,2}):(\d{2})\s*[-~\u301C]\s*(\d{1,2}):(\d{2})$/.exec(String(slot || '').trim());
    if (!match) {
        return null;
    }
    const [, sh, sm, eh, em] = match.map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    if (sh > 23 || eh > 23 || sm > 59 || em > 59 || end <= start) {
        return null;
    }
    return {
        start,
        end,
        startHour: sh,
        // An event ending exactly on the hour does not occupy that hour's row.
        lastHour: em > 0 ? eh : eh - 1,
        label: `${pad2(sh)}:${pad2(sm)}-${pad2(eh)}:${pad2(em)}`,
        endLabel: `${pad2(eh)}:${pad2(em)}`,
    };
}

/**
 * Is this date a Japanese public holiday?
 *
 * @param {string} iso      YYYY-MM-DD.
 * @param {Object} holidays Map of date => holiday name, from PHP.
 * @returns {boolean}
 */
export function isHoliday(iso, holidays) {
    return Boolean(holidays && Object.prototype.hasOwnProperty.call(holidays, iso));
}

/**
 * Is the park shut on this date?
 *
 * An explicit entry from the business-hours sheet always wins — it carries
 * both the regular 定休日 and one-off 臨時休業, and it records holiday opening
 * on days that would normally be closed. Only when the sheet does not reach a
 * date does the weekly fallback apply, and a public holiday overrides that.
 *
 * @param {Date}   date           Date to test.
 * @param {Object} closedDays     Map of date => reason, from the sheet.
 * @param {Array}  closedWeekdays Weekday numbers normally closed.
 * @param {Object} holidays       Map of date => holiday name.
 * @returns {{closed: boolean, reason: string}}
 */
export function closureFor(date, closedDays, closedWeekdays, holidays) {
    const iso = toISODate(date);

    if (closedDays && Object.prototype.hasOwnProperty.call(closedDays, iso)) {
        return { closed: true, reason: closedDays[iso] };
    }
    if (closedDays && Object.keys(closedDays).length > 0) {
        // The sheet covers this span and says nothing, so the park is open.
        const dates = Object.keys(closedDays);
        if (iso >= dates[0] && iso <= dates[dates.length - 1]) {
            return { closed: false, reason: '' };
        }
    }
    if ((closedWeekdays || []).includes(date.getDay()) && !isHoliday(iso, holidays)) {
        return { closed: true, reason: '定休日' };
    }
    return { closed: false, reason: '' };
}

/**
 * Classify a date as a weekday or a weekend/holiday.
 *
 * Saturdays, Sundays and Japanese public holidays (including 振替休日 and
 * 国民の休日) all count as weekend_holiday.
 *
 * @param {Date}   date     Date to classify.
 * @param {Object} holidays Map of date => holiday name.
 * @returns {'weekday'|'weekend_holiday'}
 */
export function dayTypeForDate(date, holidays) {
    const day = date.getDay();
    if (day === 0 || day === 6 || isHoliday(toISODate(date), holidays)) {
        return 'weekend_holiday';
    }
    return 'weekday';
}

/**
 * Leading blank count and length for a month grid.
 *
 * @param {string} monthValue YYYY-MM.
 * @returns {{year:number,month:number,firstWeekday:number,daysInMonth:number}|null}
 */
export function getMonthMeta(monthValue) {
    const match = /^(\d{4})-(\d{2})$/.exec(String(monthValue || ''));
    if (!match) {
        return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        return null;
    }
    return {
        year,
        month,
        firstWeekday: new Date(year, month - 1, 1).getDay(),
        daysInMonth: new Date(year, month, 0).getDate(),
    };
}

/**
 * Add days to a YYYY-MM-DD string.
 *
 * @param {string} iso  Date string.
 * @param {number} days Days to add.
 * @returns {string}
 */
export function addDays(iso, days) {
    const date = parseISODate(iso);
    if (!date) {
        return iso;
    }
    date.setDate(date.getDate() + days);
    return toISODate(date);
}

/**
 * Every date from start to end, inclusive.
 *
 * @param {string} start YYYY-MM-DD.
 * @param {string} end   YYYY-MM-DD.
 * @returns {Array} Date strings.
 */
export function datesBetween(start, end) {
    const out = [];
    if (!parseISODate(start) || !parseISODate(end) || end < start) {
        return out;
    }
    let cursor = start;
    // Guard against a runaway range from bad data.
    while (cursor <= end && out.length < 400) {
        out.push(cursor);
        cursor = addDays(cursor, 1);
    }
    return out;
}

/**
 * Short day label for listing dates, e.g. 9月22日(火).
 *
 * @param {string} iso    YYYY-MM-DD.
 * @param {string} locale Active locale.
 * @returns {string}
 */
export function formatShortDate(iso, locale) {
    const date = parseISODate(iso);
    if (!date) {
        return iso;
    }
    if (locale === 'en') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    const weekday = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
    return `${date.getMonth() + 1}月${date.getDate()}日(${weekday})`;
}

/**
 * Localised date label for headings.
 *
 * @param {Date}   date   Date to format.
 * @param {string} locale 'ja' or 'en'.
 * @returns {string}
 */
export function formatDateLabel(date, locale) {
    if (locale === 'en') {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
}
